import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Check, Loader2, Search } from "lucide-react";
import {
  getRetailerWebhooks,
  updateRetailerWebhooks,
  type RetailerWebhookConfig,
} from "../../services/AppConfigService";
import { getDiscordWebhooks } from "../../services/DiscordWebhookService";

const CONFIG_QUERY_KEY = ["retailer-webhooks"] as const;

export default function RetailerWebhooksTab() {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<RetailerWebhookConfig[]>([]);
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const configQuery = useQuery({ queryKey: CONFIG_QUERY_KEY, queryFn: getRetailerWebhooks });
  const webhookQuery = useQuery({ queryKey: ["admin-discord-webhooks"], queryFn: getDiscordWebhooks });

  useEffect(() => {
    if (!configQuery.data) return;
    setRows(configQuery.data.retailers);
    setDirty(false);
  }, [configQuery.data]);

  const mutation = useMutation({
    mutationFn: updateRetailerWebhooks,
    onSuccess: (response) => {
      queryClient.setQueryData(CONFIG_QUERY_KEY, response);
      setRows(response.retailers);
      setDirty(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
  });

  const normalizedSearch = search.trim().toLowerCase();
  const visibleRows = useMemo(() => rows.filter((row) => (
    !normalizedSearch
    || row.displayName.toLowerCase().includes(normalizedSearch)
    || row.retailer.toLowerCase().includes(normalizedSearch)
  )), [normalizedSearch, rows]);
  const hooks = webhookQuery.data?.webhooks ?? [];
  const assignedCount = rows.filter((row) => row.webhookId).length;

  const updateRow = (retailer: string, webhookId: string) => {
    setRows((current) => current.map((row) => row.retailer === retailer ? { ...row, webhookId } : row));
    setDirty(true);
    setSaved(false);
  };

  if (configQuery.isLoading || webhookQuery.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  if (configQuery.isError || webhookQuery.isError || !configQuery.data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
        Retailer webhook configuration could not be loaded.
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <BellRing className="h-4 w-4 text-gray-500 dark:text-dark-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Retailer webhooks</h2>
          </div>
          <p className="max-w-2xl text-xs leading-5 text-gray-500 dark:text-dark-400">
            Choose a Discord destination for each online retailer. Qualifying products at 50% off or more send a rich notification with a protected DealHawk affiliate link.
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
          {assignedCount} assigned
        </span>
      </div>

      {configQuery.data.configMissing && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
          No retailer assignments have been saved yet. Select destinations below, then save changes.
        </div>
      )}

      {hooks.length === 0 && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
          Add a named destination in the Discord webhooks tab before assigning retailers here.
        </div>
      )}

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search online retailers"
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100 dark:border-dark-700 dark:bg-dark-900 dark:text-white dark:focus:border-red-500 dark:focus:ring-red-950"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900">
        <div className="hidden grid-cols-[minmax(180px,1fr)_minmax(240px,1fr)] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:border-dark-700 dark:bg-dark-800/70 dark:text-dark-400 sm:grid">
          <span>Online retailer</span>
          <span>Notification webhook</span>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-dark-800">
          {visibleRows.map((row) => (
            <div key={row.retailer} className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(180px,1fr)_minmax(240px,1fr)] sm:items-center sm:gap-4">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">{row.displayName}</div>
                <span className="font-mono text-[10px] text-gray-400 dark:text-dark-500">{row.retailer}</span>
              </div>
              <label>
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-gray-400 sm:hidden">Notification webhook</span>
                <select
                  aria-label={`${row.displayName} notification webhook`}
                  value={row.webhookId}
                  onChange={(event) => updateRow(row.retailer, event.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs font-medium text-gray-700 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 dark:border-dark-700 dark:bg-dark-950 dark:text-dark-200 dark:focus:border-red-500 dark:focus:ring-red-950"
                >
                  <option value="">Notifications off</option>
                  {hooks.map((hook) => <option key={hook.id} value={hook.id}>{hook.name}</option>)}
                </select>
              </label>
            </div>
          ))}
          {visibleRows.length === 0 && <p className="px-4 py-10 text-center text-sm text-gray-400">No retailers match that search.</p>}
        </div>
      </div>

      <div className="sticky bottom-3 mt-4 flex items-center gap-3 rounded-xl border border-gray-200 bg-white/95 px-3 py-3 shadow-lg backdrop-blur dark:border-dark-700 dark:bg-dark-900/95">
        <button
          type="button"
          onClick={() => mutation.mutate(rows)}
          disabled={!dirty || mutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="h-3.5 w-3.5" /> : null}
          {saved ? "Saved" : "Save changes"}
        </button>
        {dirty && !mutation.isPending && <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>}
        {mutation.isError && <span className="text-xs text-red-600 dark:text-red-400">Failed to save changes</span>}
      </div>
    </div>
  );
}
